import { AdvancedMarker, ControlPosition, Map as GoogleMap, useMap } from '@vis.gl/react-google-maps';
import { ListIcon, XIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import colors from 'tailwindcss/colors';
import type { AssetWithStats } from '@/common/asset';
import { googleThemeMapping } from '@/components/map/googleMapTheme';
import { useTheme } from '@/context/ThemeContext';
import { cn } from '../../../../lib/utils';
import { getConditionRatingColorsMapping } from '../../../../lib/utils/asset';
import { Button } from '../../../ui/button';

export interface AssetGoogleMapProps {
	assets: AssetWithStats[];
	selectedAsset: AssetWithStats | null;
	onSelectAsset: (asset: AssetWithStats) => void;
	showSidePanel?: boolean;
	toggleSidePanel?: () => void;
	leftPanelWidth?: number;
}

const getConditionRatingBgColorsMapping = (rating: number) => {
	if (rating < 2) {
		return 'green';
	} else if (rating < 3 && rating >= 2) {
		return 'yellow';
	} else {
		return 'red';
	}
};

// Create custom marker icon HTML
const MarkerIconHtml = ({ asset, isSelected }: { asset: AssetWithStats; isSelected: boolean }) => {
	const { t } = useTranslation();
	const conditionRatingColorsMapping = getConditionRatingBgColorsMapping(asset.conditionRating || 0);
	const color = isSelected ? colors.blue[600] : colors[conditionRatingColorsMapping][600];

	return (
		<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
			<title>{t('assets.map.markerTitle', { name: asset.name })}</title>
			<path
				d="M12.5 0C5.596 0 0 5.596 0 12.5c0 9.375 12.5 28.125 12.5 28.125S25 21.875 25 12.5C25 5.596 19.404 0 12.5 0z"
				fill={color}
				stroke={isSelected ? '#ffffff' : '#000000'}
				strokeWidth="1.5"
				vectorEffect="non-scaling-stroke"
			/>
			<circle cx="12.5" cy="12.5" r="5.5" fill="white" />
			{isSelected && <circle cx="12.5" cy="12.5" r="2.5" fill={color} />}
		</svg>
	);
};

export const AssetGoogleMap: React.FC<AssetGoogleMapProps> = ({
	assets,
	selectedAsset,
	onSelectAsset,
	showSidePanel,
	toggleSidePanel,
	leftPanelWidth,
}) => {
	const googleMap = useMap();
	const { theme } = useTheme();

	const leftPanelOffset = showSidePanel ? leftPanelWidth || 0 : 50;

	useEffect(() => {
		// Wait until the map is ready
		if (!googleMap || !assets?.length) {
			return;
		}

		const filteredAssets = assets.filter((asset) => asset.latitude && asset.longitude);

		// If there is no data, fallback to zooming into Germany on a high level
		if (filteredAssets.length === 0) {
			googleMap.setCenter({ lat: 51.165691, lng: 10.451526 });
			googleMap.setZoom(4);
			return;
		}

		const bounds = new google.maps.LatLngBounds();

		filteredAssets.forEach((asset) => {
			bounds.extend({
				lat: asset.latitude as number,
				lng: asset.longitude as number,
			});
		});

		// Fit the map to the bounds of the assets
		googleMap.fitBounds(bounds, {
			top: 50,
			right: 50,
			bottom: 50,
			left: leftPanelOffset, // offsets dynamically based on panel width
		});
	}, [googleMap, assets, leftPanelOffset]);

	useEffect(() => {
		if (!googleMap || !assets?.length) {
			return;
		}

		if (!selectedAsset?.latitude || !selectedAsset?.longitude) return;

		const targetZoom = 17;
		googleMap.setZoom(targetZoom);

		const latLng = new google.maps.LatLng(selectedAsset.latitude, selectedAsset.longitude);
		const projection = googleMap.getProjection();

		const offsetPixels = leftPanelOffset / 2 || 0;

		if (projection && offsetPixels > 0) {
			const point = projection.fromLatLngToPoint(latLng);
			if (point) {
				const scale = 2 ** targetZoom;

				// Subtracting from the X coordinate moves the camera to the left,
				// which makes the marker appear to move to the right.
				const offsetPoint = new google.maps.Point(point.x - offsetPixels / scale, point.y);

				const offsetCenterLatLng = projection.fromPointToLatLng(offsetPoint);
				if (offsetCenterLatLng) {
					googleMap.panTo(offsetCenterLatLng);
					return;
				}
			}
		}

		googleMap.panTo(latLng);
	}, [selectedAsset?.latitude, selectedAsset?.longitude, googleMap, leftPanelOffset]);

	const onMarkerClick = (asset: AssetWithStats) => {
		onSelectAsset(asset);
	};

	return (
		<div className="relative h-full w-full">
			<div className="absolute top-15 left-2 z-50">
				{toggleSidePanel && (
					<Button
						variant="outline"
						size="icon"
						className="mb-2 hover:bg-primary hover:text-primary-foreground cursor-pointer"
						onClick={toggleSidePanel}
					>
						{showSidePanel ? <XIcon className="size-4" /> : <ListIcon className="size-4" />}
					</Button>
				)}
			</div>
			{/* Also added the default center here so it starts in Germany before useEffect even fires */}
			<GoogleMap
				className="h-full w-full"
				gestureHandling="greedy"
				defaultCenter={{ lat: 51.165691, lng: 10.451526 }}
				defaultZoom={6}
				mapId="DEMO_MAP_ID"
				mapTypeControl={true}
				colorScheme={googleThemeMapping[theme]}
				mapTypeControlOptions={{
					position: ControlPosition.TOP_RIGHT,
				}}
				minZoom={3}
				maxZoom={20}
			>
				{assets.map((asset) => {
					const isSelected = asset?.id === selectedAsset?.id;

					return (
						<AdvancedMarker
							key={asset.id}
							position={{ lat: asset.latitude as number, lng: asset.longitude as number }}
							title={asset.name}
							zIndex={isSelected ? 1000 : 20}
						>
							{/* 
							Fixed-size container (25x41 matches the SVG). 
							This ensures AdvancedMarker anchors the exact bottom-center to the map coordinate.
						*/}
							<div
								className="relative w-[25px] h-[41px] flex justify-center cursor-pointer group"
								onClick={() => onMarkerClick(asset)}
							>
								{/* Tooltip */}
								<div
									className={cn(
										'absolute flex items-center whitespace-nowrap rounded-md px-2 py-1 shadow-md border transition-all duration-300 origin-bottom',
										isSelected
											? 'bottom-[55px] bg-primary text-primary-foreground border-primary z-10 scale-105'
											: 'bottom-[45px] bg-card text-foreground border-border scale-100 opacity-90 group-hover:opacity-100 group-hover:bottom-[48px]',
									)}
								>
									<span className="text-xs font-semibold mr-2">{asset.name}</span>
									<span
										className={cn(
											'flex h-4 w-4 items-center justify-center rounded-[4px] text-[10px] font-bold',
											isSelected
												? 'bg-background/25 text-primary-foreground'
												: cn('bg-muted/50', getConditionRatingColorsMapping(asset.conditionRating)),
										)}
									>
										{asset.conditionRating}
									</span>

									{/* Tooltip Tail */}
									<div
										className={cn(
											'absolute -bottom-[5px] left-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-b border-r',
											isSelected ? 'bg-primary border-primary' : 'bg-card border-border',
										)}
									/>
								</div>

								<div
									className={cn(
										'absolute bottom-0 transition-transform duration-300 origin-bottom',
										isSelected ? 'scale-[1.2] drop-shadow-xl z-10' : 'scale-100 drop-shadow-md',
									)}
								>
									<MarkerIconHtml asset={asset} isSelected={isSelected} />
								</div>
							</div>
						</AdvancedMarker>
					);
				})}
			</GoogleMap>
		</div>
	);
};
